import { useEffect, useMemo, useState } from 'react';

import { Box } from '../Box';
import { Button } from '../Button/Button';
import { ButtonIcon } from '../ButtonIcon';
import { IconSolidCheveronLeft, IconSolidCheveronRight, IconSolidDotsHorizontal } from '../icons';

import { firstButton, lastButton, middleButton, selected, simple } from './style.css';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function range(start: number, end: number, step?: number): number[] {
  const $step = step ?? 1;
  const length = Math.floor((end - start) / $step);
  return Array.from({ length }, (_, idx) => idx * $step + start);
}

export const PAGE_SIZE = 10;
export const DEFAULT_SIBLING_COUNT = 2;
export const START_PAGE = 1;

const OPENSEARCH_MAX_RESULTS = 10000;
const MAX_PAGES = Math.floor(OPENSEARCH_MAX_RESULTS / PAGE_SIZE) - 1;

type SetPageType = (newPage: number) => void;

interface Props {
  page?: number;
  setPage: SetPageType | React.Dispatch<React.SetStateAction<number>>;
  totalCount: number;
  pageSize?: number;
  siblingCount?: number;
  loading?: boolean;
}

enum ExpandAction {
  Back = 'Back',
  Forward = 'Forward',
}

export const SearchPagination = ({ page, setPage, totalCount, pageSize, siblingCount, loading }: Props) => {
  const currentPage = page ?? START_PAGE;
  const $pageSize = pageSize ?? PAGE_SIZE;
  const $siblingCount = siblingCount ?? DEFAULT_SIBLING_COUNT;

  const [resultCount, setResultCount] = useState(totalCount);

  useEffect(() => {
    if (!loading) {
      setResultCount(totalCount);
    }
  }, [totalCount, loading]);

  const pageCount = Math.min(MAX_PAGES, Math.ceil(resultCount / $pageSize));

  const skip = (offset: number) => {
    const newPage = clamp((page || START_PAGE) + offset, START_PAGE, pageCount);
    return setPage(newPage);
  };

  // startIdx | dots | siblings | currentIdx | siblings | dots | endIdx
  const sideItemCount = 1 + $siblingCount + 1 + $siblingCount;

  const content = useMemo(() => {
    // startIdx | dots | siblings | currentIdx | siblings | dots | endIdx
    const totalItemCount = 2 + $siblingCount + 1 + $siblingCount + 2;
    if (pageCount <= totalItemCount) {
      return range(START_PAGE, pageCount + 1);
    }

    const leftmostSiblingPage = Math.max(currentPage - $siblingCount, START_PAGE);
    const rightmostSiblingPage = Math.min(currentPage + $siblingCount, START_PAGE + pageCount - 1);

    const showLeftDots = leftmostSiblingPage > START_PAGE + 1;
    const showRightDots = rightmostSiblingPage < START_PAGE + pageCount - 2;

    if (showLeftDots && showRightDots) {
      const middleRange = range(leftmostSiblingPage, rightmostSiblingPage + 1);
      return [START_PAGE, ExpandAction.Back, ...middleRange, ExpandAction.Forward, pageCount];
    }

    if (!showLeftDots && showRightDots) {
      const leftRange = range(START_PAGE, sideItemCount);

      return [...leftRange, ExpandAction.Forward, pageCount];
    }

    const rightRange = range(pageCount - sideItemCount + 1, pageCount + 1);
    return [START_PAGE, ExpandAction.Back, ...rightRange];
  }, [$siblingCount, currentPage, sideItemCount, pageCount]);

  if (!pageCount) {
    return null;
  }

  return (
    <Box display="flex" justifyContent="space-between" gap="8" borderTop="secondary" px="12" width="full" py="6">
      <ButtonIcon
        kind="secondary"
        emphasis="medium"
        size="small"
        shape="thin"
        disabled={currentPage <= START_PAGE}
        onClick={() => {
          skip(-1);
        }}
        icon={<IconSolidCheveronLeft size={14} />}
      />
      <Box display="flex" overflow="hidden" flexGrow={1}>
        {content.map((val: any, idx: number) => {
          const dotSkip = sideItemCount - 1;
          const isFirst = idx === 0;
          const isLast = idx === content.length - 1;
          const isSelected = val === currentPage;

          switch (val) {
            case ExpandAction.Forward:
            case ExpandAction.Back:
              return (
                <ButtonIcon
                  kind="secondary"
                  size="small"
                  shape="thin"
                  emphasis="medium"
                  icon={<IconSolidDotsHorizontal size={14} />}
                  cssClass={[simple, middleButton]}
                  key={idx}
                  onClick={() => (val === ExpandAction.Forward ? skip(dotSkip) : skip(-dotSkip))}
                />
              );
            default:
              return (
                <Button
                  cssClass={[
                    simple,
                    {
                      [firstButton]: isFirst,
                      [lastButton]: isLast,
                      [middleButton]: !isFirst && !isLast,
                      [selected]: isSelected,
                    },
                  ]}
                  key={idx}
                  onClick={() => setPage(val)}
                  size="small"
                  emphasis="medium"
                  kind="secondary"
                >
                  {val}
                </Button>
              );
          }
        })}
      </Box>
      <ButtonIcon
        kind="secondary"
        emphasis="medium"
        size="small"
        shape="thin"
        disabled={currentPage >= pageCount}
        onClick={() => {
          skip(1);
        }}
        icon={<IconSolidCheveronRight size={14} />}
      />
    </Box>
  );
};
